import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, AuthTokens } from '@asset-manager/shared'
import { authApi } from '@/api/auth'

const AUTH_USER_KEY = 'auth_user'
const AUTH_TOKENS_KEY = 'auth_tokens'

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref<User | null>(null)
  const tokens = ref<AuthTokens | null>(null)
  const isLoading = ref(false)

  // 计算属性
  const isAuthenticated = computed(() => !!user.value && !!tokens.value)
  const accessToken = computed(() => tokens.value?.accessToken)
  /** 当前用户是否为管理员（由服务端响应注入，持久化于 localStorage） */
  const isAdmin = computed(() => user.value?.role === 'admin')

  /**
   * 检查 localStorage 中是否存在有效 Token（不依赖响应式状态）
   * 用于路由守卫等需要同步检查的场景
   */
  const isLoggedIn = computed(() => {
    if (tokens.value?.accessToken) return true
    // 降级检查 localStorage（store 尚未初始化时）
    try {
      const saved = localStorage.getItem(AUTH_TOKENS_KEY)
      if (!saved) return false
      const parsed: AuthTokens = JSON.parse(saved)
      return !!parsed?.accessToken
    } catch {
      return false
    }
  })

  // 动作
  const login = async (contact: string, code: string, type: 'email' | 'phone') => {
    isLoading.value = true
    try {
      const response = type === 'email' 
        ? await authApi.verifyEmail(contact, code)
        : await authApi.verifyPhone(contact, code)
      
      if (response.data) {
        user.value = response.data.user
        tokens.value = response.data.tokens
        
        // 保存到localStorage
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user.value))
        localStorage.setItem(AUTH_TOKENS_KEY, JSON.stringify(tokens.value))
        
        return { success: true }
      }
      return { success: false, message: response.message }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, message: '登录失败，请重试' }
    } finally {
      isLoading.value = false
    }
  }

  const sendVerificationCode = async (contact: string, type: 'email' | 'phone') => {
    isLoading.value = true
    try {
      const response = type === 'email'
        ? await authApi.sendEmailCode(contact)
        : await authApi.sendSmsCode(contact)
      
      return { success: response.code === 200, message: response.message }
    } catch (error) {
      console.error('Send code error:', error)
      return { success: false, message: '发送验证码失败，请重试' }
    } finally {
      isLoading.value = false
    }
  }

  const refreshToken = async () => {
    if (!tokens.value?.refreshToken) return false
    
    try {
      const response = await authApi.refreshToken(tokens.value.refreshToken)
      if (response.data?.tokens) {
        tokens.value = response.data.tokens
        localStorage.setItem(AUTH_TOKENS_KEY, JSON.stringify(tokens.value))
        return true
      }
      // 刷新接口返回但没有 tokens，清除登录态
      user.value = null
      tokens.value = null
      localStorage.removeItem(AUTH_USER_KEY)
      localStorage.removeItem(AUTH_TOKENS_KEY)
      return false
    } catch (error) {
      console.error('Refresh token error:', error)
      // 刷新失败，清除登录态防止无限重试
      user.value = null
      tokens.value = null
      localStorage.removeItem(AUTH_USER_KEY)
      localStorage.removeItem(AUTH_TOKENS_KEY)
      return false
    }
  }

  const logout = async () => {
    try {
      if (tokens.value?.refreshToken) {
        await authApi.logout(tokens.value.refreshToken)
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // 清除本地状态
      user.value = null
      tokens.value = null
      localStorage.removeItem(AUTH_USER_KEY)
      localStorage.removeItem(AUTH_TOKENS_KEY)
    }
  }

  /**
   * Web 端退出登录（需求 7.2）
   * 仅撤销 Web 端 Token，不影响小程序端会话
   * 调用 /logout 接口（携带 accessToken），然后清除本地存储
   */
  const webLogout = async () => {
    try {
      if (tokens.value?.accessToken) {
        // /logout 路由通过 authMiddleware 从 Authorization header 获取 accessToken
        // 并根据 Token 中的 client 字段仅撤销对应终端的 RefreshToken
        await authApi.logout(tokens.value.refreshToken ?? '')
      }
    } catch (error) {
      console.error('Web logout error:', error)
    } finally {
      // 清除本地 Web 端 Token 状态
      user.value = null
      tokens.value = null
      localStorage.removeItem(AUTH_USER_KEY)
      localStorage.removeItem(AUTH_TOKENS_KEY)
    }
  }

  /**
   * 保存 Web 端 QR 扫码登录后获取的 Token（需求 6.5）
   * 由 useQrLogin composable 在轮询到 confirmed 状态时调用
   */
  const setWebTokens = (newTokens: AuthTokens, newUser: User) => {
    tokens.value = newTokens
    user.value = newUser
    localStorage.setItem(AUTH_TOKENS_KEY, JSON.stringify(newTokens))
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(newUser))
  }

  const initializeAuth = () => {
    try {
      const savedUser = localStorage.getItem(AUTH_USER_KEY)
      const savedTokens = localStorage.getItem(AUTH_TOKENS_KEY)
      
      if (savedUser && savedTokens) {
        user.value = JSON.parse(savedUser)
        tokens.value = JSON.parse(savedTokens)
      }
    } catch (error) {
      console.error('Initialize auth error:', error)
      // 清除损坏的数据
      localStorage.removeItem(AUTH_USER_KEY)
      localStorage.removeItem(AUTH_TOKENS_KEY)
    }
  }

  /**
   * 从服务端刷新用户信息（role 等字段可能在 DB 中被修改，localStorage 缓存会过期）
   * 应在 initializeAuth 之后异步调用，失败时不影响正常使用。
   */
  const refreshUserFromServer = async () => {
    if (!tokens.value?.accessToken) return
    try {
      const response = await authApi.getCurrentUser()
      if (response.data?.user) {
        user.value = response.data.user
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user.value))
      }
    } catch {
      // 静默失败：token 过期等情况由 axios 拦截器处理
    }
  }

  /**
   * 从 localStorage 恢复 Token（应用初始化时调用）
   * 与 initializeAuth 功能相同，提供语义更清晰的别名
   */
  const loadTokensFromStorage = () => {
    initializeAuth()
  }

  return {
    // 状态
    user,
    tokens,
    isLoading,
    // 计算属性
    isAuthenticated,
    isLoggedIn,
    isAdmin,
    accessToken,
    // 动作
    login,
    sendVerificationCode,
    refreshToken,
    logout,
    webLogout,
    setWebTokens,
    loadTokensFromStorage,
    initializeAuth,
    refreshUserFromServer
  }
})